import { formatEra } from "./utils";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

// Pitch-type column labels. Indices are 1-based (chip 1 = FB, chip 2 = CH, etc.)
// to match the server-side `pitcher_chip` / `batter_chip` values that feed the
// per-type tally. When pitcher.pitch_chips < 4 (custom pitcher with fewer types)
// the unused columns simply read 0.
const PITCH_TYPE_COLS = [
  { chip: 1, abbr: "FB" },
  { chip: 2, abbr: "CH" },
  { chip: 3, abbr: "CB" },
  { chip: 4, abbr: "SL" },
];

const sumTypeCounts = (typesMap) =>
  PITCH_TYPE_COLS.reduce((acc, col) => acc + ((typesMap || {})[col.chip] || 0), 0);

// Exported so VictoryModal can embed it without a second modal
export function GameStatsContent({ game, gameStats }) {
  const awayTeam = game.away_team;
  const homeTeam = game.home_team;
  const playerStats = gameStats.players || {};

  const getStats = (playerId) => playerStats[playerId] || {};

  const BatterRow = ({ player, index }) => {
    const s = getStats(player.id);
    const ab = s.ab || 0, h = s.h || 0, k = s.k || 0, bb = s.bb || 0, rbi = s.rbi || 0;
    const singles = s.singles || 0, doubles = s.doubles || 0, triples = s.triples || 0, hr = s.hr || 0;
    const sb = s.sb || 0;
    const mgr = s.mgrUsed || 0, chips = s.chipsUsed || 0;
    const guesses = s.guessTypes || {};
    const guessTotal = sumTypeCounts(guesses);
    const avg = ab > 0 ? (h / ab).toFixed(3).replace(/^0/, '') : '.000';
    return (
      <tr className="border-b border-slate-800/50 hover:bg-slate-800/30">
        <td className="py-1 px-2 text-left whitespace-nowrap">
          <span className="text-[10px] text-slate-500 mr-1">{index + 1}</span>
          <span className="text-xs text-slate-300">{player.name}</span>
          <span className="text-[9px] text-slate-600 ml-1">{player.position}</span>
        </td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-slate-400">{ab}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-emerald-400 font-bold">{h}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-slate-400">{singles}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-slate-400">{doubles}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-slate-400">{triples}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-amber-400 font-bold">{hr}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-cyan-400">{rbi}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-emerald-300">{sb || ''}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-red-400">{k}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-blue-400">{bb}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-white font-bold">{avg}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-purple-400">{mgr || ''}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-pink-400">{chips || ''}</td>
        {/* Guess-type columns: count of each chip the batter picked across all pitches faced. */}
        {PITCH_TYPE_COLS.map(col => (
          <td key={col.chip} className="py-1 px-0.5 text-center text-xs font-mono text-amber-300/80">{guesses[col.chip] || ''}</td>
        ))}
        <td className="py-1 px-0.5 text-center text-xs font-mono text-amber-200 font-bold">{guessTotal || ''}</td>
      </tr>
    );
  };

  const PitcherRow = ({ player }) => {
    const s = getStats(player.id);
    const k = s.k || 0, bb = s.bb || 0, h = s.h || 0, outs = s.outs || 0, ra = s.runsAllowed || 0;
    const mgr = s.mgrUsed || 0, chips = s.chipsUsed || 0;
    const types = s.pitchTypes || {};
    const typeTotal = sumTypeCounts(types);
    const era = formatEra(ra, outs);
    const ip = `${Math.floor(outs / 3)}.${outs % 3}`;
    return (
      <tr className="border-b border-slate-800/50 hover:bg-slate-800/30">
        <td className="py-1 px-2 text-left whitespace-nowrap">
          <span className="text-xs text-slate-300">{player.name}</span>
        </td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-slate-400">{ip}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-red-400 font-bold">{k}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-blue-400">{bb}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-amber-400">{h}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-rose-300">{ra}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-white font-bold">{era}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-purple-400">{mgr || ''}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-pink-400">{chips || ''}</td>
        {/* Pitch-mix columns: count of each pitch type thrown. */}
        {PITCH_TYPE_COLS.map(col => (
          <td key={col.chip} className="py-1 px-0.5 text-center text-xs font-mono text-red-300/80">{types[col.chip] || ''}</td>
        ))}
        <td className="py-1 px-0.5 text-center text-xs font-mono text-red-200 font-bold">{typeTotal || ''}</td>
      </tr>
    );
  };

  const TeamSection = ({ team, batters, pitchers, teamColor }) => (
    <div className="mb-4">
      <h3 className={`text-sm font-bold ${teamColor} mb-2`}>{team.name}</h3>
      <div className="mb-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[9px] text-slate-500 border-b border-slate-700">
              <th className="py-1 px-2 text-left">Batter</th>
              <th className="py-1 px-0.5 text-center">AB</th><th className="py-1 px-0.5 text-center">H</th>
              <th className="py-1 px-0.5 text-center">1B</th><th className="py-1 px-0.5 text-center">2B</th>
              <th className="py-1 px-0.5 text-center">3B</th><th className="py-1 px-0.5 text-center">HR</th>
              <th className="py-1 px-0.5 text-center">RBI</th><th className="py-1 px-0.5 text-center">SB</th>
              <th className="py-1 px-0.5 text-center">SO</th>
              <th className="py-1 px-0.5 text-center">BB</th><th className="py-1 px-0.5 text-center">AVG</th>
              <th className="py-1 px-0.5 text-center">MGR</th><th className="py-1 px-0.5 text-center">CHP</th>
              {PITCH_TYPE_COLS.map(col => (
                <th key={col.chip} className="py-1 px-0.5 text-center" title={`Times batter guessed ${col.abbr}`}>{col.abbr}</th>
              ))}
              <th className="py-1 px-0.5 text-center" title="Total chip guesses">TOT</th>
            </tr>
          </thead>
          <tbody>{batters.slice(0, 9).map((p, i) => <BatterRow key={p.id} player={p} index={i} />)}</tbody>
        </table>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[9px] text-slate-500 border-b border-slate-700">
              <th className="py-1 px-2 text-left">Pitcher</th>
              <th className="py-1 px-0.5 text-center">IP</th><th className="py-1 px-0.5 text-center">SO</th>
              <th className="py-1 px-0.5 text-center">BB</th><th className="py-1 px-0.5 text-center">H</th>
              <th className="py-1 px-0.5 text-center">R</th><th className="py-1 px-0.5 text-center">ERA</th>
              <th className="py-1 px-0.5 text-center">MGR</th><th className="py-1 px-0.5 text-center">CHP</th>
              {PITCH_TYPE_COLS.map(col => (
                <th key={col.chip} className="py-1 px-0.5 text-center" title={`${col.abbr} pitches thrown`}>{col.abbr}</th>
              ))}
              <th className="py-1 px-0.5 text-center" title="Total pitches thrown">TOT</th>
            </tr>
          </thead>
          <tbody>{pitchers.map(p => <PitcherRow key={p.id} player={p} />)}</tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <TeamSection team={awayTeam} batters={awayTeam.players.filter(p => p.position !== "P")} pitchers={awayTeam.players.filter(p => p.position === "P")} teamColor="text-blue-400" />
      <TeamSection team={homeTeam} batters={homeTeam.players.filter(p => p.position !== "P")} pitchers={homeTeam.players.filter(p => p.position === "P")} teamColor="text-red-400" />
    </div>
  );
}

export default function GameStatsModal({ open, onClose, game, gameStats }) {
  if (!open) return null;

  const awayTeam = game.away_team;
  const homeTeam = game.home_team;
  const playerStats = gameStats.players || {};

  const awayBatters = awayTeam.players.filter(p => p.position !== "P");
  const homeBatters = homeTeam.players.filter(p => p.position !== "P");
  const awayPitchers = awayTeam.players.filter(p => p.position === "P");
  const homePitchers = homeTeam.players.filter(p => p.position === "P");

  const getStats = (playerId) => playerStats[playerId] || {};

  const BatterRow = ({ player, index }) => {
    const s = getStats(player.id);
    const ab = s.ab || 0, h = s.h || 0, k = s.k || 0, bb = s.bb || 0, rbi = s.rbi || 0;
    const singles = s.singles || 0, doubles = s.doubles || 0, triples = s.triples || 0, hr = s.hr || 0;
    const sb = s.sb || 0;
    const mgr = s.mgrUsed || 0, chips = s.chipsUsed || 0;
    const guesses = s.guessTypes || {};
    const guessTotal = sumTypeCounts(guesses);
    const avg = ab > 0 ? (h / ab).toFixed(3).replace(/^0/, '') : '.000';
    return (
      <tr className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`batter-row-${index}`}>
        <td className="py-1 px-2 text-left whitespace-nowrap">
          <span className="text-[10px] text-slate-500 mr-1">{index + 1}</span>
          <span className="text-xs text-slate-300">{player.name}</span>
          <span className="text-[9px] text-slate-600 ml-1">{player.position}</span>
        </td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-slate-400">{ab}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-emerald-400 font-bold">{h}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-slate-400">{singles}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-slate-400">{doubles}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-slate-400">{triples}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-amber-400 font-bold">{hr}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-cyan-400">{rbi}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-emerald-300">{sb || ''}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-red-400">{k}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-blue-400">{bb}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-white font-bold">{avg}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-purple-400">{mgr || ''}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-pink-400">{chips || ''}</td>
        {PITCH_TYPE_COLS.map(col => (
          <td key={col.chip} className="py-1 px-0.5 text-center text-xs font-mono text-amber-300/80">{guesses[col.chip] || ''}</td>
        ))}
        <td className="py-1 px-0.5 text-center text-xs font-mono text-amber-200 font-bold">{guessTotal || ''}</td>
      </tr>
    );
  };

  const PitcherRow = ({ player }) => {
    const s = getStats(player.id);
    const k = s.k || 0, bb = s.bb || 0, h = s.h || 0, outs = s.outs || 0, ra = s.runsAllowed || 0;
    const mgr = s.mgrUsed || 0, chips = s.chipsUsed || 0;
    const types = s.pitchTypes || {};
    const typeTotal = sumTypeCounts(types);
    const era = formatEra(ra, outs);
    const ip = `${Math.floor(outs / 3)}.${outs % 3}`;
    return (
      <tr className="border-b border-slate-800/50 hover:bg-slate-800/30" data-testid={`pitcher-row-${player.name}`}>
        <td className="py-1 px-2 text-left whitespace-nowrap">
          <span className="text-xs text-slate-300">{player.name}</span>
        </td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-slate-400">{ip}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-red-400 font-bold">{k}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-blue-400">{bb}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-amber-400">{h}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-rose-300">{ra}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-white font-bold">{era}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-purple-400">{mgr || ''}</td>
        <td className="py-1 px-0.5 text-center text-xs font-mono text-pink-400">{chips || ''}</td>
        {PITCH_TYPE_COLS.map(col => (
          <td key={col.chip} className="py-1 px-0.5 text-center text-xs font-mono text-red-300/80">{types[col.chip] || ''}</td>
        ))}
        <td className="py-1 px-0.5 text-center text-xs font-mono text-red-200 font-bold">{typeTotal || ''}</td>
      </tr>
    );
  };

  const TeamSection = ({ team, batters, pitchers, teamColor }) => (
    <div className="mb-4">
      <h3 className={`text-sm font-bold ${teamColor} mb-2`}>{team.name}</h3>
      
      {/* Batters Table */}
      <div className="mb-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[9px] text-slate-500 border-b border-slate-700">
              <th className="py-1 px-2 text-left">Batter</th>
              <th className="py-1 px-0.5 text-center">AB</th>
              <th className="py-1 px-0.5 text-center">H</th>
              <th className="py-1 px-0.5 text-center">1B</th>
              <th className="py-1 px-0.5 text-center">2B</th>
              <th className="py-1 px-0.5 text-center">3B</th>
              <th className="py-1 px-0.5 text-center">HR</th>
              <th className="py-1 px-0.5 text-center">RBI</th>
              <th className="py-1 px-0.5 text-center">SB</th>
              <th className="py-1 px-0.5 text-center">SO</th>
              <th className="py-1 px-0.5 text-center">BB</th>
              <th className="py-1 px-0.5 text-center">AVG</th>
              <th className="py-1 px-0.5 text-center" title="Manager points used">MGR</th>
              <th className="py-1 px-0.5 text-center" title="Chips used">CHP</th>
              {PITCH_TYPE_COLS.map(col => (
                <th key={col.chip} className="py-1 px-0.5 text-center" title={`Times batter guessed ${col.abbr}`}>{col.abbr}</th>
              ))}
              <th className="py-1 px-0.5 text-center" title="Total chip guesses">TOT</th>
            </tr>
          </thead>
          <tbody>
            {batters.slice(0, 9).map((player, idx) => (
              <BatterRow key={player.id} player={player} index={idx} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pitchers Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[9px] text-slate-500 border-b border-slate-700">
              <th className="py-1 px-2 text-left">Pitcher</th>
              <th className="py-1 px-0.5 text-center">IP</th>
              <th className="py-1 px-0.5 text-center">SO</th>
              <th className="py-1 px-0.5 text-center">BB</th>
              <th className="py-1 px-0.5 text-center">H</th>
              <th className="py-1 px-0.5 text-center">R</th>
              <th className="py-1 px-0.5 text-center">ERA</th>
              <th className="py-1 px-0.5 text-center" title="Manager points used">MGR</th>
              <th className="py-1 px-0.5 text-center" title="Chips used">CHP</th>
              {PITCH_TYPE_COLS.map(col => (
                <th key={col.chip} className="py-1 px-0.5 text-center" title={`${col.abbr} pitches thrown`}>{col.abbr}</th>
              ))}
              <th className="py-1 px-0.5 text-center" title="Total pitches thrown">TOT</th>
            </tr>
          </thead>
          <tbody>
            {pitchers.map((player) => (
              <PitcherRow key={player.id} player={player} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="game-stats-modal-overlay"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden"
          data-testid="game-stats-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
            <h2 className="text-lg font-bold text-white">Box Score</h2>
            <button onClick={onClose} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white transition-colors" data-testid="close-stats-modal">
              <X size={18} />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(85vh-60px)]">
            {/* Teams — stacked single-column so each box-score table gets the
                full modal width and never needs a horizontal scroll. */}
            <div className="grid grid-cols-1 gap-4">
              <TeamSection team={awayTeam} batters={awayBatters} pitchers={awayPitchers} teamColor="text-blue-400" />
              <TeamSection team={homeTeam} batters={homeBatters} pitchers={homePitchers} teamColor="text-red-400" />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
